import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import AdminLayout from '../../components/AdminLayout';
import axiosInstance from '../../api/axios';

/* ─── helpers ─────────────────────────────────────────────── */
const initialFilters = {
  tanggal_mulai: '',
  tanggal_akhir: '',
  status: '',
  metode_bayar: '',
  search: '',
};

const formatDate = (value) => {
  if (!value) return '-';
  return new Date(value).toLocaleDateString('id-ID', {
    day: '2-digit', month: 'long', year: 'numeric',
  });
};

const fmtRupiah = (n) =>
  new Intl.NumberFormat('id-ID', {
    style: 'currency', currency: 'IDR', minimumFractionDigits: 0,
  }).format(n ?? 0);

const STATUS_LABEL = {
  pending_billing: 'Menunggu Input Tagihan',
  billed:          'Tagihan Siap',
  draft:           'Menunggu Konfirmasi',
  paid:            'Lunas',
  cancelled:       'Dibatalkan',
};

const STATUS_COLOR = {
  pending_billing: '#0369a1',
  billed:          '#b45309',
  draft:           '#854d0e',
  paid:            '#166534',
  cancelled:       '#991b1b',
};

const STATUS_BG = {
  pending_billing: '#f0f9ff',
  billed:          '#fef3c7',
  draft:           '#fef9c3',
  paid:            '#dcfce7',
  cancelled:       '#fee2e2',
};

/* ─── StatusPill ──────────────────────────────────────────── */
function StatusPill({ status }) {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      padding: '4px 10px', borderRadius: 20,
      fontSize: 11, fontWeight: 600,
      background: STATUS_BG[status] ?? '#f1f5f9',
      color: STATUS_COLOR[status] ?? '#475569',
      whiteSpace: 'nowrap',
    }}>
      <span style={{
        width: 6, height: 6, borderRadius: '50%',
        background: STATUS_COLOR[status] ?? '#94a3b8',
        display: 'inline-block', flexShrink: 0,
      }} />
      {STATUS_LABEL[status] ?? status}
    </span>
  );
}

/* ─── Main Component ──────────────────────────────────────── */
const LaporanTransaksi = () => {
  const [filters,         setFilters]         = useState(initialFilters);
  const [laporan,         setLaporan]         = useState([]);
  const [summary,         setSummary]         = useState(null);
  const [total,           setTotal]           = useState(0);
  const [loading,         setLoading]         = useState(false);
  const [exporting,       setExporting]       = useState(false);
  const [error,           setError]           = useState('');

  /* ── fetch data ── */
  const fetchLaporan = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = { ...filters };
      Object.keys(params).forEach((k) => { if (!params[k]) delete params[k]; });

      const res = await axiosInstance.get('/admin/laporan/transaksi', { params });
      const body = res.data;

      setLaporan(body.data ?? []);
      setSummary(body.summary ?? null);
      setTotal(body.total ?? 0);
    } catch (err) {
      console.error(err);
      setError('Gagal mengambil data laporan transaksi. Silakan coba lagi.');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { fetchLaporan(); }, [fetchLaporan]);

  /* ── handlers ── */
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const resetFilters = () => setFilters(initialFilters);

  const downloadBlob = (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const a   = document.createElement('a');
    a.href    = url;
    a.setAttribute('download', filename);
    document.body.appendChild(a);
    a.click();
    a.parentNode.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const handleExportPdf = async () => {
    setExporting(true);
    setError('');
    try {
      const params = { ...filters, tipe: 'transaksi' };
      Object.keys(params).forEach((k) => { if (!params[k]) delete params[k]; });

      const res = await axiosInstance.get('/admin/laporan/export/pdf', {
        params,
        responseType: 'blob',
      });
      downloadBlob(res.data, 'laporan-transaksi.pdf');
    } catch (err) {
      console.error(err);
      setError('Gagal mengekspor laporan transaksi ke PDF.');
    } finally {
      setExporting(false);
    }
  };

  /* ── summary helpers ── */
  const totalPendapatan  = summary?.total_pendapatan  ?? 0;
  const totalTransaksi   = summary?.total_transaksi   ?? total;
  const transaksiLunas   = summary?.transaksi_lunas   ?? 0;
  const transaksiPending = summary?.transaksi_pending ?? 0;

  /* ── distribusi status ── */
  const distribusiStatus = summary?.distribusi_status ?? [];

  /* ── render ── */
  return (
    <AdminLayout title="Laporan Pendapatan Klinik">
      <div className="dashboard-section">

        {/* Header */}
        <div className="section-header">
          <h2>Ringkasan Pendapatan Klinik</h2>
          <Link to="/admin/alur-kerja" className="view-all">Kembali ke dashboard laporan →</Link>
        </div>

        <p>
          Gunakan filter di bawah ini untuk melihat laporan transaksi berdasarkan
          periode, status pembayaran, dan metode bayar.
        </p>

        {/* Filter form */}
        <div className="form">
          <div className="form-group">
            <label htmlFor="tanggal_mulai">Tanggal Mulai</label>
            <input
              id="tanggal_mulai" name="tanggal_mulai" type="date"
              value={filters.tanggal_mulai} onChange={handleChange}
            />
          </div>

          <div className="form-group">
            <label htmlFor="tanggal_akhir">Tanggal Akhir</label>
            <input
              id="tanggal_akhir" name="tanggal_akhir" type="date"
              value={filters.tanggal_akhir} onChange={handleChange}
            />
          </div>

          <div className="form-group">
            <label htmlFor="status">Status</label>
            <select id="status" name="status" value={filters.status} onChange={handleChange}>
              <option value="">Semua</option>
              <option value="pending_billing">Menunggu Input Tagihan</option>
              <option value="billed">Tagihan Siap</option>
              <option value="draft">Menunggu Konfirmasi</option>
              <option value="paid">Lunas</option>
              <option value="cancelled">Dibatalkan</option>
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="metode_bayar">Metode Bayar</label>
            <select id="metode_bayar" name="metode_bayar" value={filters.metode_bayar} onChange={handleChange}>
              <option value="">Semua</option>
              <option value="tunai">Tunai</option>
              <option value="transfer">Transfer</option>
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="search">Cari</label>
            <input
              id="search" name="search" type="search"
              placeholder="No. invoice, nama pasien"
              value={filters.search} onChange={handleChange}
              autoComplete="off"
            />
          </div>

          <div className="form-actions">
            <button type="button" className="btn" onClick={fetchLaporan}>
              Terapkan Filter
            </button>
            <button type="button" className="btn" onClick={handleExportPdf} disabled={exporting}>
              {exporting ? 'Mengekspor PDF…' : 'Export PDF'}
            </button>
            <button type="button" className="btn danger" onClick={resetFilters}>
              Reset Filter
            </button>
          </div>
        </div>

        {error && <p className="form-error">{error}</p>}

        {/* Stats + Tabel */}
        <div className="dashboard-section" style={{ padding: '20px 0 0 0', boxShadow: 'none', background: 'transparent' }}>

          {/* Stat cards */}
          <div className="stats-grid" style={{ gap: '12px', marginBottom: '16px', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))' }}>
            <div className="stat-card" style={{ borderLeftColor: '#3498db', padding: '14px 12px', display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div className="stat-icon" style={{ fontSize: '20px' }}>🧾</div>
              <div className="stat-info">
                <h3 style={{ fontSize: '12px', margin: 0, color: '#64748b' }}>Total Transaksi</h3>
                <p className="stat-value" style={{ fontSize: '16px', margin: '2px 0 0 0' }}>{totalTransaksi.toLocaleString()}</p>
              </div>
            </div>
            <div className="stat-card" style={{ borderLeftColor: '#2ecc71', padding: '14px 12px', display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div className="stat-icon" style={{ fontSize: '20px' }}>💰</div>
              <div className="stat-info">
                <h3 style={{ fontSize: '12px', margin: 0, color: '#64748b' }}>Total Pendapatan</h3>
                <p className="stat-value" style={{ fontSize: '14px', margin: '2px 0 0 0', fontWeight: 600 }}>{fmtRupiah(totalPendapatan)}</p>
              </div>
            </div>
            <div className="stat-card" style={{ borderLeftColor: '#27ae60', padding: '14px 12px', display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div className="stat-icon" style={{ fontSize: '20px' }}>✅</div>
              <div className="stat-info">
                <h3 style={{ fontSize: '12px', margin: 0, color: '#64748b' }}>Transaksi Lunas</h3>
                <p className="stat-value" style={{ fontSize: '16px', margin: '2px 0 0 0' }}>{transaksiLunas.toLocaleString()}</p>
              </div>
            </div>
            <div className="stat-card" style={{ borderLeftColor: '#e67e22', padding: '14px 12px', display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div className="stat-icon" style={{ fontSize: '20px' }}>⏳</div>
              <div className="stat-info">
                <h3 style={{ fontSize: '12px', margin: 0, color: '#64748b' }}>Belum Selesai</h3>
                <p className="stat-value" style={{ fontSize: '16px', margin: '2px 0 0 0' }}>{transaksiPending.toLocaleString()}</p>
              </div>
            </div>
          </div>

          {/* Distribusi status */}
          {distribusiStatus.length > 0 && (
            <div className="dashboard-section" style={{ padding: '20px', marginBottom: '20px' }}>
              <h3>Distribusi Status Transaksi</h3>
              <div className="activity-list">
                {distribusiStatus.map((item) => (
                  <div
                    key={item.status}
                    className="activity-item"
                    style={{ background: '#fff' }}
                  >
                    <span className="activity-icon">🧾</span>
                    <div className="activity-info">
                      <p>{STATUS_LABEL[item.status] ?? item.status}</p>
                      <span className="time">Total: {item.total}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Tabel hasil */}
          <div className="dashboard-section" style={{ padding: '24px', background: '#ffffff', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <h2 style={{ marginBottom: '16px', fontSize: '18px', fontWeight: 600 }}>Hasil Laporan Transaksi</h2>
            {loading ? (
              <p style={{ padding: '20px 0', color: '#64748b' }}>Memuat data laporan...</p>
            ) : (
              <div style={{ overflowX: 'auto', borderRadius: '6px', border: '1px solid #e2e8f0' }}>
                <table
                  className="table-report"
                  style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '13px' }}
                >
                  <thead>
                    <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                      <th style={{ padding: '12px 16px', color: '#475569', fontWeight: 600 }}>No. Invoice</th>
                      <th style={{ padding: '12px 16px', color: '#475569', fontWeight: 600 }}>Pasien</th>
                      <th style={{ padding: '12px 16px', color: '#475569', fontWeight: 600 }}>Dokter</th>
                      <th style={{ padding: '12px 16px', color: '#475569', fontWeight: 600 }}>Metode</th>
                      <th style={{ padding: '12px 16px', color: '#475569', fontWeight: 600, textAlign: 'right' }}>Total</th>
                      <th style={{ padding: '12px 16px', color: '#475569', fontWeight: 600, textAlign: 'center' }}>Status</th>
                    </tr>
                  </thead>
                  <tbody style={{ color: '#334155' }}>
                    {laporan.length > 0 ? (
                      laporan.map((item) => (
                        <tr 
                          key={item.id ?? item.nomor_invoice}
                          style={{ borderBottom: '1px solid #f1f5f9' }}
                        >
                          <td style={{ padding: '12px 16px' }}>
                            <span style={{ fontFamily: 'monospace', fontWeight: 600, fontSize: 12, color: '#2563eb' }}>
                              {item.nomor_invoice ?? '-'}
                            </span>
                          </td>
                          <td style={{ padding: '12px 16px', fontWeight: 500 }}>{item.pendaftaran?.pasien?.nama ?? item.pasien?.nama ?? '-'}</td>
                          <td style={{ padding: '12px 16px' }}>{item.pendaftaran?.dokter?.nama ?? '-'}</td>
                          <td style={{ padding: '12px 16px' }}>
                            {item.metode_bayar ? (
                              <span style={{
                                display: 'inline-flex', alignItems: 'center', gap: 4,
                                padding: '3px 8px', borderRadius: 12, fontSize: 11, fontWeight: 600,
                                background: item.metode_bayar === 'tunai' ? '#fef3c7' : '#dbeafe',
                                color:      item.metode_bayar === 'tunai' ? '#b45309'  : '#1e40af',
                                whiteSpace: 'nowrap'
                              }}>
                                {item.metode_bayar === 'tunai' ? '💵 Tunai' : '🏦 Transfer'}
                              </span>
                            ) : '-'}
                          </td>
                          <td style={{ padding: '12px 16px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                            <strong style={{ color: item.status === 'paid' ? '#166534' : '#0f172a' }}>
                              {item.total > 0 ? fmtRupiah(item.total) : '-'}
                            </strong>
                          </td>
                          <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                            <StatusPill status={item.status} />
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="6" style={{ textAlign: 'center', padding: '32px', color: '#64748b' }}>
                          Tidak ada data transaksi yang sesuai filter.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

      </div>
    </AdminLayout>
  );
};

export default LaporanTransaksi;